import { useCallback, useEffect, useRef, useState } from 'react';

import { ErrorType } from 'api/AuthApi';
import { PhoneNumbersLabels } from 'enums/PhoneNumbers';

export const UNSUBSCRIBED_PHONE_NUMBER_MESSAGE = `You've unsubscribed from SMS messages. To receive messages again, text "START" to ${PhoneNumbersLabels.Support}`;

const UNSUBSCRIBED_BLOCK_DURATION_MS = 5000;

const isUnsubscribedError = (error: unknown): boolean =>
  (error as { errorType?: ErrorType })?.errorType === ErrorType.UnsubscribedPhoneNumber;

export const useUnsubscribedPhoneNumber = () => {
  const [isUnsubscribedPhoneNumber, setIsUnsubscribedPhoneNumber] = useState(false);
  const [isSendCodeBlocked, setIsSendCodeBlocked] = useState(false);
  const blockTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (blockTimeoutRef.current) clearTimeout(blockTimeoutRef.current);
    },
    [],
  );

  const resetUnsubscribedWarning = useCallback(() => {
    setIsUnsubscribedPhoneNumber(false);
  }, []);

  // Returns true when the error was the "unsubscribed phone number" error so callers
  // can skip their generic error handling.
  const handleSendSmsCodeError = useCallback((error: unknown): boolean => {
    if (!isUnsubscribedError(error)) return false;

    setIsUnsubscribedPhoneNumber(true);
    setIsSendCodeBlocked(true);

    if (blockTimeoutRef.current) clearTimeout(blockTimeoutRef.current);

    blockTimeoutRef.current = setTimeout(() => setIsSendCodeBlocked(false), UNSUBSCRIBED_BLOCK_DURATION_MS);

    return true;
  }, []);

  return {
    isUnsubscribedPhoneNumber,
    isSendCodeBlocked,
    handleSendSmsCodeError,
    resetUnsubscribedWarning,
  };
};
